import React, { useState } from 'react';
import { CHILD_FEATURES, PRIVACY_FEATURES, DATA_COLLECTION, DATA_USAGE, THIRD_PARTY_SERVICES, PARENTAL_RIGHTS, COPPA_NOTE, CONSENT_MAPPING } from './consentQuestionsSimple';

export default function SimpleConsentForm({ onAgree, onCancel, isSubmitting = false }) {
  const [showDetails, setShowDetails] = useState(false);

  const handleAgree = async () => {
    // Map to backend consent format
    await onAgree(CONSENT_MAPPING);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-6 sm:p-8 max-w-3xl w-full shadow-2xl border border-white/20 relative overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Decorative gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50/30 via-transparent to-amber-100/20 rounded-3xl"></div>
        <div className="relative z-10">
          {/* Header */}
          <div className="text-center mb-6">
            <h3 className="text-3xl font-bold bg-gradient-to-r from-amber-700 to-amber-600 bg-clip-text text-transparent mb-2">
              Welcome to Artmosphere!
            </h3>
            <p className="text-slate-600 text-base">
              Parent approval required for children under 13
            </p>
          </div>

          {/* What Your Child Can Do */}
          <div className="mb-6">
            <h4 className="text-xl font-bold text-slate-800 mb-4">
              What Your Child Can Do
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {CHILD_FEATURES.map((feature, idx) => (
                <div key={idx} className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                  <div>
                    <h5 className="font-semibold text-slate-800 mb-1">{feature.title}</h5>
                    <p className="text-sm text-slate-600">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Privacy & Safety Features */}
          <div className="mb-6 p-5 bg-green-50 border-2 border-green-200 rounded-xl">
            <h4 className="text-xl font-bold text-green-900 mb-4">
              Privacy & Safety Features
            </h4>
            <div className="space-y-3">
              {PRIVACY_FEATURES.items.map((item, idx) => (
                <div key={idx} className="flex items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-800">{item.label}:</span>
                      <span className="text-green-700 font-bold">{item.value}</span>
                    </div>
                    <p className="text-sm text-slate-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Expandable COPPA Details */}
          <div className="mb-6">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="w-full p-4 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200 transition-all flex items-center justify-between"
            >
              <span className="font-semibold text-slate-700">
                {showDetails ? 'Hide' : 'Show'} Privacy Details
              </span>
              <span className="text-xl">{showDetails ? '▲' : '▼'}</span>
            </button>
            
            {showDetails && (
              <div className="mt-4 p-5 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
                <div>
                  <h5 className="font-bold text-slate-800 mb-2">{DATA_COLLECTION.title}</h5>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-600">
                    {DATA_COLLECTION.items.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-slate-500 mt-2">{DATA_COLLECTION.note}</p>
                </div>
                
                <div>
                  <h5 className="font-bold text-slate-800 mb-2">{DATA_USAGE.title}</h5>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-600">
                    {DATA_USAGE.items.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h5 className="font-bold text-slate-800 mb-2">{THIRD_PARTY_SERVICES.title}</h5>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-600">
                    {THIRD_PARTY_SERVICES.items.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-slate-500 mt-2">{THIRD_PARTY_SERVICES.note}</p>
                </div>
              </div>
            )}
          </div>

          {/* Parental Rights */}
          <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
            <h4 className="font-bold text-blue-900 mb-3">{PARENTAL_RIGHTS.title}</h4>
            <div className="space-y-2">
              {PARENTAL_RIGHTS.items.map((item, idx) => (
                <div key={idx} className="text-sm text-blue-800">
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* COPPA Note */}
          <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-900">
              <strong>{COPPA_NOTE.title}:</strong> {COPPA_NOTE.text}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={onCancel}
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 border-2 border-slate-300 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleAgree}
              disabled={isSubmitting}
              className="flex-1 px-8 py-3 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-700 hover:to-amber-600 text-white rounded-xl text-base font-bold transition-all shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:scale-100 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Processing...</span>
                </>
              ) : (
                'I Agree'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
